import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useSession } from '@/stores/session';

/**
 * Routing.
 *
 * The guard resolves the session before deciding anything, so a reload on a protected
 * page lands back on that page rather than bouncing through sign-in. A `redirect`
 * query parameter carries the intended destination when a sign-in *is* required.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: { public: true, chrome: false },
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/views/SignInView.vue'),
    meta: { public: true, chrome: false, guestOnly: true },
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: () => import('@/views/SignUpView.vue'),
    meta: { public: true, chrome: false, guestOnly: true },
  },
  {
    path: '/welcome',
    name: 'onboarding',
    component: () => import('@/views/OnboardingView.vue'),
    meta: { chrome: false },
  },
  { path: '/dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
  { path: '/pods', name: 'pods', component: () => import('@/views/PodsView.vue') },
  { path: '/pods/new', name: 'pod-create', component: () => import('@/views/PodCreateView.vue') },
  { path: '/pods/:id', name: 'pod-detail', component: () => import('@/views/PodDetailView.vue') },
  { path: '/volumes', name: 'volumes', component: () => import('@/views/VolumesView.vue') },
  { path: '/ssh-keys', name: 'ssh-keys', component: () => import('@/views/SshKeysView.vue') },
  { path: '/billing', name: 'billing', component: () => import('@/views/BillingView.vue') },
  {
    path: '/billing/invoices/:id',
    name: 'invoice',
    component: () => import('@/views/InvoiceView.vue'),
  },
  { path: '/settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
});

router.beforeEach(async (to) => {
  const session = useSession();

  // Resolve the session once, before any redirect decision. Guessing here is what
  // makes protected pages flicker through sign-in on reload.
  if (session.status === 'unknown') {
    await session.refresh();
  }

  const authenticated = session.status === 'authenticated';

  if (to.meta.guestOnly === true && authenticated) {
    return { name: session.needsOnboarding ? 'onboarding' : 'dashboard' };
  }

  if (to.meta.public !== true && !authenticated) {
    return { name: 'sign-in', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } };
  }

  // Send a new account through onboarding, but never trap them there: every other
  // route stays reachable, and once onboarding is skipped or finished the dashboard is
  // too. Without the dismissal check, Skip and "Go to dashboard" bounced back here.
  if (
    authenticated &&
    session.needsOnboarding &&
    to.name === 'dashboard' &&
    session.me?.onboarding.has_pod === false
  ) {
    return { name: 'onboarding' };
  }

  return true;
});
